/**
 * Generates a structured prompt for AI-based advertising copy generation
 * @param {Object} params - Parameters for the ad copy generation
 * @param {string} params.speech - Speech form to use ('du' for informal or 'sie' for formal German)
 * @param {string} params.productName - The product or service being advertised
 * @param {boolean} params.isLeadFunnel - Whether this is for customer acquisition (true) or recruitment (false)
 * @param {string} params.funnelURL - URL for the call-to-action
 * @param {string} params.specialInstruction - Optional special instructions for the AI
 * @param {string} params.companyName - Company name for the advertisement
 * @param {string} params.tonality - Desired tone for the ad copy
 * @param {string} params.example1 - Optional first example ad
 * @param {string} params.example2 - Optional second example ad
 * @param {string} params.example3 - Optional third example ad
 * @param {boolean} params.shouldUseEmoji - Whether to include emojis in the generated content
 * @returns {Array} Array of prompt objects for the AI service
 */
export const generateInstruction = (params) => {
  const {
    speech,
    productName,
    isLeadFunnel,
    funnelURL,
    specialInstruction = "",
    companyName,
    tonality,
    example1,
    example2,
    example3,
    shouldUseEmoji
  } = params;
  const examples = [example1, example2, example3];
  const SPEECH_WORD = speech === "du" ? "Du-Form" : "Sie-Form";
  const FUNNEL_TYPE = isLeadFunnel
    ? "Neukundengewinnung"
    : "Mitarbeitergewinnung";
  let systemPrompt;
  if (example1 || example2 || example3) {
    systemPrompt = {
      role: "system",
      content:
        "Du bist ein KI-unterstützter Copywriting Profi. Du bekommst von mir drei Beispiele für hochkonvertierende Werbeanzeigen, die in der Vergangenheit sehr gut konvertiert haben. Schreibe mir basierend auf diesen Beispielen Werbeanzeigen für eine andere Firma, im Stil meiner Beispiele."
    };
  } else {
    systemPrompt = {
      role: "system",
      content:
        "Verhalte dich wie ein KI-unterstützter Copywriting-Profi. Du bekommst von mir eine Anweisung und sollst damit hochkonvertierende Facebook Werbeanzeigen schreiben."
    };
  }
  const examplesPrompt = [];
  examples.forEach((content) => {
    if (content) {
      examplesPrompt.push({ role: "user", content, type: "example" });
    }
  });
  const prompt = {
    role: "user",
    content: `Schreibe anhand meiner Beispiele 3 unterschiedliche Werbeanzeigen mit den folgenden Angaben:
Firma: ${companyName}
Ziel: ${FUNNEL_TYPE}
Produkt: ${productName}
Emojis verwenden: ${shouldUseEmoji}
Tonalität: ${tonality}${
      specialInstruction &&
      `
Spezielle Anweisungen: ${specialInstruction}`
    }
Ansprache: ${SPEECH_WORD}
URL für CTA: ${funnelURL}
Wichtig: Gib mir die Antworten jeweils nacheinander in dreifachen curly brackets zurück. Do not use "1." or "1)" as enumeration!`
  };
  const payload = [systemPrompt, ...examplesPrompt, prompt];
  return payload;
};
